import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

// Celebration moment shown when the Brand Persona is finalized (§9.7): a burst of
// brand-coloured confetti plus a scale/fade "concluída ✦" overlay. Fires once
// while `show` is true, then calls `onDone` after ~1.8s so the caller can redirect.
const CONFETTI_COLORS = ["#A16BF7", "#D6C2FD", "#ffffff"];

export default function OnboardingCelebration({
  show,
  title = "Brand Persona concluída ✦",
  duration = 1800,
  onDone,
}) {
  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const confetti = (await import("canvas-confetti")).default;
        if (cancelled) return;
        confetti({ particleCount: 160, spread: 82, startVelocity: 45, origin: { y: 0.62 }, colors: CONFETTI_COLORS });
        setTimeout(() => {
          if (!cancelled) confetti({ particleCount: 90, spread: 100, angle: 60, origin: { x: 0, y: 0.7 }, colors: CONFETTI_COLORS });
          if (!cancelled) confetti({ particleCount: 90, spread: 100, angle: 120, origin: { x: 1, y: 0.7 }, colors: CONFETTI_COLORS });
        }, 220);
      } catch {
        /* confetti is non-critical — proceed to redirect regardless */
      }
    })();

    const timer = setTimeout(() => { if (!cancelled) onDone?.(); }, duration);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {show && (
        <motion.div
          data-testid="onboarding-celebration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[60] flex items-center justify-center px-6"
          style={{ background: "rgba(10,8,16,0.82)", backdropFilter: "blur(10px)" }}
        >
          <motion.div
            initial={{ scale: 0.7, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 18 }}
            className="text-center"
          >
            <motion.div
              animate={{ scale: [1, 1.12, 1] }}
              transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
              className="mx-auto mb-5 w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
              style={{
                background: "linear-gradient(135deg, #B58BFA 0%, #A16BF7 60%, #7A45E8 100%)",
                color: "#0A0810",
                boxShadow: "0 0 44px -6px rgba(161,107,247,0.7)",
              }}
            >
              ✦
            </motion.div>
            <h2 className="font-display text-3xl sm:text-4xl text-brand-ink">{title}</h2>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
